import * as React from "react"
import {
  InputGroup,
  InputLeftElement,
  Flex,
  Input,
  Text
} from "@chakra-ui/react"
import { StreamRow, StartStreamingButton } from "./stream-container.styled"
import { Stream } from "./stream-container.interfaces"
import { DeleteButton, DeleteIcon } from "../../../styles/common.styled"
import { RoomBranding } from "../../../../recoil/atoms/room/types"
import { FaRegTrashAlt } from "react-icons/fa"
import { SDKContext } from "../../../../context/SDKContext"
import * as Yup from "yup"
import { CommonInput } from "../../../common/fields"
import { AiTwotoneEdit } from "react-icons/ai"
import { Icon } from "../../../common/icons"
import { useTranslation } from "react-i18next"

interface StreamItemProp {
  pickUpIcon: (rtmpUrl: string) => React.ReactNode
  stream: Stream
  isStartStream: boolean
  roomStyles: RoomBranding
  setCurrentStream: (stream: Stream) => void
}

export const StreamItem: React.FC<StreamItemProp> = ({
  stream,
  pickUpIcon,
  isStartStream,
  roomStyles,
  setCurrentStream
}) => {
  const meetingClient = React.useContext(SDKContext)
  const [label, setLabel] = React.useState<string>("")
  const [rtmpUrl, setRtmpUrl] = React.useState<string>("")
  const [isTouch, setIsTouch] = React.useState<boolean>(false)
  const [errorRTMP, setErrorRTMP] = React.useState<string>("")
  const [errorLabel, setErrorLabel] = React.useState<string>("")
  const { t } = useTranslation()

  React.useEffect(() => {
    setLabel(stream.label)
    setRtmpUrl(stream.rtmpUrl)
  }, [stream.label, stream.rtmpUrl])

  const schemaRTMP = Yup.string()
    .test("MustbeRTMPURL", t("Must be RTMP URL"), function () {
      return rtmpUrl.startsWith("rtmps://")
    })
    .max(1024, "MaxRTMP")
    .nullable()

  const schemaLabel = Yup.string().max(1024, "MaxLabel").nullable()

  const handleSaveItem = async e => {
    try {
      e.preventDefault()
      const validRtmpUrl = await schemaRTMP.validate(rtmpUrl)
      const validLabel = await schemaLabel.validate(label)
      meetingClient.updateStreamConfig(stream.id, validLabel, validRtmpUrl)
      setErrorRTMP("")
      setErrorLabel("")
    } catch (e) {
      if (e.message.includes("RTMP URL")) {
        setErrorRTMP("Must be RTMP URL")
      }
      if (e.message.includes("MaxRTMP")) {
        setErrorRTMP("max. 1024 char")
      }

      if (e.message.includes("MaxLabel")) {
        setErrorLabel("max. 1024 char")
      }
    }
  }

  const isDisableSaveButton = React.useMemo(() => {
    return (
      isStartStream ||
      (stream.label === label && stream.rtmpUrl === rtmpUrl) ||
      label.length === 0 ||
      rtmpUrl.length === 0
    )
  }, [isStartStream, label, rtmpUrl, stream.label, stream.rtmpUrl])

  const handleEdit = () => {
    setIsTouch(true)
  }

  return (
    <StreamRow style={{ alignItems: "unset" }}>
      <Flex flexDirection={"column"} width="100%">
        {!isTouch ? (
          <Text
            marginLeft="1px"
            marginBottom="4px"
            fontSize={"0.8rem"}
            onClick={handleEdit}
            color={
              roomStyles?.servicePanelElementLabels &&
              roomStyles?.servicePanelElementLabels
            }
          >
            {label}{" "}
            {
              <Icon
                src={AiTwotoneEdit}
                marginTop={"auto"}
                color={roomStyles?.servicePanelElementPrimaryText ?? "#CBD5E0"}
                verticalAlign="middle"
                marginBottom="1px"
                cursor="text"
                _hover={{
                  color: roomStyles?.servicePanelElementSecondaryText
                }}
              />
            }
          </Text>
        ) : (
          <InputGroup onBlur={() => setIsTouch(false)} marginBottom="4px">
            <Input
              id={`${stream.id}-label-input`}
              variant="flushed"
              placeholder={t("Label...")}
              height="19.2px"
              fontSize={"0.8rem"}
              marginLeft="1px"
              autoFocus
              value={label}
              onBlur={() => setIsTouch(false)}
              name={`${stream.id}-label`}
              onChange={e => setLabel(e.target.value)}
              borderBottom="2px"
              borderColor={
                errorLabel
                  ? "red"
                  : `${roomStyles?.servicePanelElementBorder}b3` ?? "inherit"
              }
              color={
                roomStyles?.servicePanelElementLabels &&
                roomStyles?.servicePanelElementLabels
              }
              _hover={{
                borderColor: errorLabel
                  ? roomStyles?.servicePanelElementWarningText ?? "red"
                  : roomStyles?.servicePanelElementBorder ?? "gray.300"
              }}
              _focus={{
                borderColor: errorLabel
                  ? roomStyles?.servicePanelElementWarningText ?? "red"
                  : roomStyles?.servicePanelElementBorder ?? "gray.500"
              }}
              _placeholder={{
                color:
                  roomStyles?.servicePanelElementLabels &&
                  roomStyles?.servicePanelElementLabels
              }}
            />
          </InputGroup>
        )}
        <InputGroup marginLeft="1px">
          <InputLeftElement
            pointerEvents="none"
            height="30px !important"
            width="35px"
            top="unset"
            children={pickUpIcon(stream.rtmpUrl)}
          />

          <CommonInput
            placeholder="rtmps://..."
            height="30px !important"
            paddingStart="30px"
            name={`${stream.id}-rtmps`}
            paddingEnd="10px"
            error={errorRTMP}
            isDisabled={isStartStream}
            value={rtmpUrl}
            onChange={e => setRtmpUrl(e.target.value)}
          />
        </InputGroup>
      </Flex>
      <StartStreamingButton        
      roomStyles={roomStyles}
        type="button"
        disabled={isDisableSaveButton}
        onClick={handleSaveItem}
        style={{ maxHeight: "30px", marginTop: "auto" }}
      >
        {t("Save")}
      </StartStreamingButton>
      <DeleteButton
        type="button"
        onClick={() => setCurrentStream(stream)}
        color={roomStyles?.servicePanelElementPrimaryText ?? "#CBD5E0"}
        disabled={isStartStream}
        style={{
          marginTop: "auto",
          padding: "8px",
          width: "30px",
          height: "30px"
          /* marginBottom: "2px" */
        }}
      >
        <DeleteIcon as={FaRegTrashAlt} roomStyles={roomStyles} />
      </DeleteButton>
    </StreamRow>
  )
}
